import { Button, ButtonColor, ButtonSize, Input } from "@swivl-mono/ui";
import { useState } from "react";

interface AddScheduleGroupProps {
  onAdd: (name: string) => Promise<{ ok: true; slug: string } | { ok: false; error: string }>;
}

/** Company-level only: name a new schedule group; it starts with the standard hours and every location can then set its own. */
export function AddScheduleGroup({ onAdd }: AddScheduleGroupProps): React.ReactElement {
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const handleAdd = async (): Promise<void> => {
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const result = await onAdd(name);
      if (!result.ok) {
        setError(result.error);
        return;
      }
      setName("");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Enter") {
      event.preventDefault();
      void handleAdd();
    }
  };

  return (
    <section className="rounded-lg border border-dashed border-grey-300 bg-white px-4 py-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="body-sm-alt text-grey-900">Add a schedule group</div>
          <div className="body-sm text-grey-600">
            A named set of hours, such as "Collections Hours", that sequences and escalation groups can follow. It starts at 9:00 AM–5:00 PM weekdays;
            every group and location can then set its own.
          </div>
        </div>
        <Input
          value={name}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Group name"
          disabled={busy}
          containerClassName="w-56"
        />
        <Button color={ButtonColor.ORANGE} size={ButtonSize.SMALL} disabled={busy || !name.trim()} onClick={() => void handleAdd()}>
          Add group
        </Button>
      </div>
      {error && <p className="body-xs mt-2 text-red-700">{error}</p>}
    </section>
  );
}
